import React, { useEffect, useState, useCallback, useRef } from 'react';
import useScrollLock from '../hooks/useScrollLock';
import { useT } from '../i18n';
import { getGalleryFull } from '../firebase/galleryService';
import { ITEM_KINDS, productHref, blobIdsOf } from '../constants/gallery';
import { currencySymbolOf } from '../constants/designer';
import { SocialBar } from './Social';
import OwnerBadge from './OwnerBadge';
import Comments from './Comments';
import { SOCIAL_KINDS } from '../firebase/socialService';
import { shareFromGalleryItem } from '../constants/share';
import { useAuth } from '../context/AuthContext';
import ShareToChat, { SendIcon } from './ShareToChat';
import BasketButton from './BasketButton';

// ── LIGHTBOX ─────────────────────────────────────────────────────────────────
// Full-size viewer for ONE post. Two things it does that the old one didn't:
//
// FETCHES THE FULL RENDITION. The grid only holds thumbnails; the full image
// lives in a separate document precisely so listing a gallery stays cheap. This
// is the moment it's worth fetching, so the viewer is actually full-size rather
// than a stretched 640px thumbnail.
//
// CACHES what it fetches, so paging back and forth doesn't re-read the same
// documents. The thumbnail is shown immediately underneath as a placeholder,
// which makes the load feel instant instead of blank.
// How far the photo must travel before releasing pages to the next one.
const SWIPE_ARM = 52;
// Zoom limits, and what a double-tap jumps to.
const ZOOM_MAX = 4;
const ZOOM_TAP = 2.5;
// Two taps closer together than this, and nearer than DBL_SLOP apart, are one
// double-tap rather than two separate taps.
const DBL_MS = 300;
const DBL_SLOP = 30;

export default function GalleryLightbox({ item, kind, onClose, onLikeChange }) {
  const { t } = useT();
  const { user } = useAuth();
  // The conversation picker, when someone chooses to send this to a person
  // rather than only look at it.
  const [toChat, setToChat] = useState(false);
  const [fullUrls, setFullUrls] = useState({});   // id -> data URL
  const [loading, setLoading] = useState(false);
  // Reported up by the comments panel, so the bar under the photo can show the
  // live number without opening a second listener on the same subcollection.
  const [commentCount, setCommentCount] = useState(null);
  // OPEN FROM THE START, at every size. It was closed on a narrow screen at
  // first, on the reasoning that the thread costs half the viewport there —
  // but a conversation nobody can see is a conversation nobody joins, and
  // making people find a control before they know there is anything behind it
  // hides the feature from exactly the people it is for. The photo is still
  // one tap from filling the screen, and that tap is now the informed one.
  const [showComments, setShowComments] = useState(true);
  // Tap the photo and everything else gets out of its way.
  const [zoom, setZoom] = useState(false);

  // THE PAGE BEHIND HOLDS STILL. Without this the feed scrolled freely under
  // an open photo — a flick meant to page through the gallery moved the grid
  // instead, and closing left you somewhere else entirely.
  useScrollLock(true);
  const cache = useRef({});

  // ── SOCIAL ON BOTH KINDS ───────────────────────────────────────────────────
  // A published 3D design gets the same bar and the same thread as a photo. It
  // used to be photos-only, on the reasoning that a design "has its own page
  // carrying them" — but that page is the read-only /share walkthrough, which
  // opened in another tab and carries neither, so a design was the one thing
  // here nobody could like from the feed or say anything about.
  // PER ITEM, not per lightbox. The feed shows photos and designs in one grid,
  // so its list is mixed and a single `kind` prop cannot describe it — items
  // from `fetchFeedWork` carry their own. A caller with a uniform list (the
  // profile's tabs) passes the prop and its items have none, so the prop is
  // the fallback rather than the other way round.
  const itemKind = item?.kind || kind;
  const social = !!item;

  // Two vocabularies meet here, and mixing them writes to the wrong document:
  //
  //   · LIKES AND VIEWS live on the PROJECT for a design (a published design is
  //     a snapshot; the project is the thing that persists), and on the photo
  //     itself for a photo. That is SOCIAL_KINDS.
  //   · COMMENTS hang off the PUBLIC GALLERY document either way — `photos` or
  //     `publishedDesigns` — because that is what a visitor can read.
  const isDesign = itemKind === ITEM_KINDS.DESIGN;
  // A LISTING, not just a photo. The lightbox has no idea who published what —
  // it is handed items from a gallery, a profile and the feed alike — so the
  // product fields themselves are the signal. Either one is enough: a shop may
  // quote a price with nowhere to link to, or the reverse.
  const isProduct = item?.price != null || !!item?.productUrl;
  const socialKind = isDesign ? SOCIAL_KINDS.PROJECT : SOCIAL_KINDS.PHOTO;
  const socialId = isDesign ? item?.projectId : item?.id;
  const commentCol = isDesign ? 'publishedDesigns' : 'photos';

  // ── ONE POST, AND ONLY ITS OWN PICTURES ────────────────────────────────────
  // The arrows move through THIS post's images and nothing else. They used to
  // page on into the next post once they ran out, which made them mean two
  // different things depending on where you were — and put a pair of chevrons
  // on every single-image photo, promising a carousel that was not there.
  //
  // So: no arrows at all on a one-image post (which is every designer's), and
  // on a post that has several they are exactly a carousel. Changing post means
  // closing this and opening another, which is what the grid behind it is for.
  const blobIds = blobIdsOf(item);
  const imgCount = blobIds.length;
  const [imgAt, setImgAt] = useState(0);

  // Opening a different post starts at its cover.
  useEffect(() => { setImgAt(0); }, [item?.id]);

  const go = useCallback((delta) => {
    // Wraps within the post, so the last image steps round to the first rather
    // than dead-ending on a disabled arrow. Inert on a single image.
    if (imgCount < 2) return;
    setImgAt(at => (at + delta + imgCount) % imgCount);
  }, [imgCount]);

  // ── SWIPING THROUGH THE POST'S PICTURES ────────────────────────────────────
  // What replaces the arrows on a touch device, where they are hidden (see
  // .glb-nav in GalleryManager.css) — the gesture people already reach for,
  // doing exactly what the arrows do on a desktop.
  //
  // Like them, it is live ONLY on a post that holds more than one picture. On a
  // single photo the drag is never armed at all (see onDragStart), so the
  // picture does not slide under the finger promising something that is not
  // there.
  //
  // Pointer events, not touch events: one path for finger, pen and trackpad.
  // The drag follows the finger so the gesture shows its own progress, and a
  // release short of the threshold springs back rather than silently doing
  // nothing.
  const drag = useRef(null);
  const [dx, setDx] = useState(0);

  // ── ZOOM AND PAN, IN FULL VIEW ONLY ────────────────────────────────────────
  // Full view alone cannot enlarge every photo: a landscape shot on an upright
  // phone is already the full width of the screen, so hiding the panels frees
  // vertical space it cannot use and it comes out the same size. Pinching is
  // what actually makes it bigger; panning is what makes a zoomed photo usable.
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const live = useRef(new Map());       // pointerId -> {x,y}, for the pinch
  const pinch = useRef(null);
  const lastTap = useRef(0);
  const tapAt = useRef({ x: 0, y: 0 });
  const imgRef = useRef(null);
  const figRef = useRef(null);
  const swallowClick = useRef(false);

  // Back to 1x whenever the picture it applied to is gone — paging to the next
  // photo, or leaving full view, must not inherit the last one's magnification.
  useEffect(() => { setScale(1); setPan({ x: 0, y: 0 }); }, [item?.id, zoom]);

  // ── WHERE THE PICTURE ACTUALLY IS ────────────────────────────────────────
  // `.glb-img` is a FIXED box and the photo is drawn inside it with
  // `object-fit: scale-down`, so a picture whose aspect differs from the box is
  // letterboxed: the element spans the full box, the picture does not. A badge
  // pinned to the element's corner would therefore float in the empty gutter
  // beside a portrait photo rather than sitting on it.
  //
  // These four properties describe the drawn rectangle, and the on-photo
  // overlays are positioned entirely from them (see .glb-onphoto).
  //
  // A ResizeObserver rather than a resize listener: the box also changes when
  // the comments panel opens, and it does so over a 0.24s CSS transition that
  // no event fires for. The observer follows it frame by frame, so the badges
  // track the edge instead of jumping when it settles.
  useEffect(() => {
    const img = imgRef.current;
    const fig = figRef.current;
    if (!img || !fig) return undefined;

    const apply = () => {
      const { naturalWidth: nw, naturalHeight: nh, clientWidth: bw, clientHeight: bh } = img;
      if (!nw || !nh || !bw || !bh) return;
      // `scale-down` is `contain`, except it never enlarges — a picture smaller
      // than the box is drawn at its own size, which is what the 1 caps.
      const s = Math.min(bw / nw, bh / nh, 1);
      fig.style.setProperty('--glb-img-w', `${bw}px`);
      fig.style.setProperty('--glb-img-h', `${bh}px`);
      fig.style.setProperty('--glb-fit-x', `${Math.round((bw - nw * s) / 2)}px`);
      fig.style.setProperty('--glb-fit-y', `${Math.round((bh - nh * s) / 2)}px`);
    };

    apply();
    // `load` for the natural size, which is unknown until the bytes arrive —
    // and fires again when paging swaps the src.
    img.addEventListener('load', apply);
    const ro = new ResizeObserver(apply);
    ro.observe(img);
    return () => { img.removeEventListener('load', apply); ro.disconnect(); };
  }, [item?.id, isDesign]);

  /** Keeps a zoomed photo's edges from being dragged inside the viewport. */
  const clampPan = useCallback((p) => {
    const r = imgRef.current?.getBoundingClientRect();
    if (!r) return p;
    // The rect is already scaled, so the slack is half of what overhangs.
    const maxX = Math.max(0, (r.width - window.innerWidth) / 2);
    const maxY = Math.max(0, (r.height - window.innerHeight) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, p.x)),
      y: Math.max(-maxY, Math.min(maxY, p.y)),
    };
  }, []);

  const zoomTo = useCallback((next) => {
    const s = Math.max(1, Math.min(ZOOM_MAX, next));
    setScale(s);
    setPan(p => (s === 1 ? { x: 0, y: 0 } : clampPan(p)));
  }, [clampPan]);

  const onDragStart = (e) => {
    if (e.pointerType === 'mouse') return;   // a mouse drags to select
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
    live.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // TWO FINGERS DOWN — start a pinch and abandon whatever one was doing, so
    // a swipe cannot be half-completed as the second finger lands.
    if (zoom && live.current.size === 2) {
      const [a, b] = [...live.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, scale };
      drag.current = null;
      setDx(0);
      return;
    }
    // CAPTURE above, so the rest of the gesture is delivered here even once the
    // finger has travelled off the photo. Without it a swipe that ends over
    // the comments below never fires `pointerup` on this element, and the
    // photo is left stranded mid-drag.
    // NOTHING TO SWIPE TO on a single-image post, so the drag is never armed —
    // the photo does not slide under the finger promising a picture that isn't
    // there. Panning a zoomed photo still needs the drag, hence the scale test.
    if (imgCount < 2 && scale === 1) return;
    drag.current = { x: e.clientX, y: e.clientY, axis: null, moved: false, panFrom: pan };
  };

  const onDragMove = (e) => {
    if (live.current.has(e.pointerId)) live.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // PINCH WINS while two fingers are down.
    if (pinch.current && live.current.size === 2) {
      const [a, b] = [...live.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      zoomTo(pinch.current.scale * (dist / pinch.current.dist));
      return;
    }

    const d = drag.current;
    if (!d) return;
    const mx = e.clientX - d.x;
    const my = e.clientY - d.y;
    // Locked once, on the first movement worth judging, so a gesture cannot
    // start as a scroll and turn into a page halfway through.
    if (Math.abs(mx) > 8 || Math.abs(my) > 8) d.moved = true;

    // ZOOMED IN, ONE FINGER PANS. Paging would fight it, and moving around the
    // picture is what you want at this point anyway.
    if (scale > 1) {
      setPan(clampPan({ x: d.panFrom.x + mx, y: d.panFrom.y + my }));
      return;
    }

    if (!d.axis && d.moved) d.axis = Math.abs(mx) > Math.abs(my) ? 'x' : 'y';
    if (d.axis !== 'x') return;
    // Damped: the photo trails the finger rather than matching it, which keeps
    // a flick from throwing it off the screen before the release is read.
    setDx(Math.max(-140, Math.min(140, mx * 0.6)));
  };

  const onDragEnd = (e) => {
    live.current.delete(e.pointerId);
    if (live.current.size < 2) pinch.current = null;

    const d = drag.current;
    drag.current = null;
    if (scale === 1 && d?.axis === 'x' && Math.abs(dx) >= SWIPE_ARM) go(dx < 0 ? 1 : -1);
    setDx(0);
    // A SWIPE IS NOT A TAP. `click` fires after `pointerup` on touch too, so
    // without this every page-turn would also toggle full screen.
    swallowClick.current = !!d?.moved;
  };

  const onPhotoClick = (e) => {
    if (swallowClick.current) { swallowClick.current = false; return; }

    // DOUBLE-TAP ZOOMS, the shortcut every photo viewer has. Detected from
    // clicks rather than a `dblclick` listener, which is unreliable on touch.
    const now = Date.now();
    const near = Math.hypot(e.clientX - tapAt.current.x, e.clientY - tapAt.current.y) < DBL_SLOP;
    if (zoom && now - lastTap.current < DBL_MS && near) {
      lastTap.current = 0;
      zoomTo(scale > 1 ? 1 : ZOOM_TAP);
      return;
    }
    lastTap.current = now;
    tapAt.current = { x: e.clientX, y: e.clientY };

    // A ZOOMED PHOTO DOES NOT LEAVE FULL VIEW ON A SINGLE TAP — that would
    // throw away the magnification you just set. Zoom out first, then tap.
    if (zoom) { if (scale === 1) exitZoom(); return; }
    enterZoom();
  };

  // ── THE PHOTO, FILLING THE VIEWER ──────────────────────────────────────────
  // Tapping the photo stands everything else down — comments, byline, caption
  // — and gives the picture the whole surface.
  //
  // NOT the browser's Fullscreen API. That takes over the entire page, which
  // is a bigger thing than was asked for: it hides the tab and the OS chrome,
  // needs its own way back out, and iOS Safari refuses it for anything but a
  // video, so it would behave differently on the device this matters most on.
  // A state of this component does the same job, identically everywhere.
  const enterZoom = useCallback(() => setZoom(true), []);
  const exitZoom = useCallback(() => setZoom(false), []);

  // Keyboard: arrows page, Escape steps back one level — out of the enlarged
  // photo first, and only then out of the viewer.
  useEffect(() => {
    const onKey = (e) => {
      // THE PICKER IS ON TOP AND OWNS THE KEYBOARD. It binds its own Escape at
      // the document too, so without this one press would close the picker AND
      // the viewer underneath it — and the arrows would page the photo behind
      // a dialog that is still describing the previous one.
      if (toChat) return;
      if (e.key === 'Escape') { if (zoom) exitZoom(); else onClose(); }
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [go, onClose, zoom, exitZoom, toChat]);

  // Paging to another photo must not leave a picker open that is still offering
  // to send the previous one.
  useEffect(() => { setToChat(false); }, [item?.id]);

  // The comment count belongs to the POST, so it resets when the post changes —
  // not when the carousel moves within one.
  useEffect(() => { setCommentCount(null); }, [item?.id]);

  // ── FETCH WHAT IS ON SCREEN ────────────────────────────────────────────────
  // Keyed on the BLOB, not the item: a post's five pictures are five documents,
  // and only the one being looked at is worth reading. The cover is what every
  // grid already shows, so opening a post costs exactly what it did before —
  // the rest arrive as the carousel reaches them, and never if it does not.
  const blobId = blobIds[Math.min(imgAt, imgCount - 1)];
  useEffect(() => {
    let alive = true;
    if (!blobId) return undefined;
    if (cache.current[blobId]) { setFullUrls(c => ({ ...c, [blobId]: cache.current[blobId] })); return undefined; }
    setLoading(true);
    (async () => {
      try {
        const url = await getGalleryFull(itemKind, blobId);
        if (!alive || !url) return;
        cache.current[blobId] = url;
        setFullUrls(c => ({ ...c, [blobId]: url }));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [blobId, kind]);

  if (!item) return null;
  // The thumbnail stands in only for the COVER — it is the only one the
  // metadata carries. A later image shows the cover until its own bytes land,
  // which reads as the carousel loading rather than as a blank frame.
  const src = fullUrls[blobId] || (imgAt === 0 ? item.thumbUrl : fullUrls[blobIds[0]] || item.thumbUrl);

  // DISMISS UNWINDS ONE LEVEL AT A TIME. Enlarging the photo is a step further
  // in, so the ✕ — and a press on the backdrop, and Escape — should step back
  // out of it and return the photo to its comments, not throw the whole viewer
  // away. Leaving the gallery from there took two deliberate steps' worth of
  // work and undid both.
  const dismiss = () => { if (zoom) exitZoom(); else onClose(); };

  return (
    <div
      className={`glb${zoom ? ' glb--zoom' : ''}`}
      role="dialog"
      aria-modal="true"
      onClick={dismiss}
    >
      <button
        className="glb-close"
        onClick={(e) => { e.stopPropagation(); dismiss(); }}
        aria-label={zoom ? t('gal_shrink') : t('cancel')}
        title={zoom ? t('gal_shrink') : t('cancel')}
      >✕</button>

      {/* THE STAGE holds the photo and, for a photo that can carry them, its
          comments. Two columns on a desktop wide enough for both, stacked and
          scrollable on anything narrower — see .glb-stage in GalleryManager.css.
          It also stops the backdrop's close-on-click, so a click anywhere in
          here (including the comment box) does not shut the viewer. */}
      <div
        className={`glb-stage${social && showComments ? ' glb-stage--open' : ''}${zoom ? ' glb-stage--zoom' : ''}`}
        onClick={e => e.stopPropagation()}
      >
      <figure className="glb-figure" ref={figRef}>
        {/* THE ARROWS BELONG TO THE PHOTO, so they are positioned against the
            figure. Against the viewport instead they sat at 50% of the screen
            — which, once comments stack underneath on a narrow layout, is in
            the middle of the conversation rather than on the picture.

            ONLY WHEN THERE IS SOMETHING TO MOVE TO. A single-image post — every
            designer's, and most shops' — gets no arrows at all: a chevron on a
            photo that cannot turn is a control advertising a feature the post
            does not have. */}
        {imgCount > 1 && (
          <>
            <button
              className="glb-nav glb-nav--prev"
              onClick={e => { e.stopPropagation(); go(-1); }}
              aria-label={t('gal_prev')}
            >‹</button>
            <button
              className="glb-nav glb-nav--next"
              onClick={e => { e.stopPropagation(); go(1); }}
              aria-label={t('gal_next')}
            >›</button>
          </>
        )}
        {/* A 3D DESIGN IS SHOWN AS THE REAL THING, not a snapshot of it.
            The published record carries a rendered preview, which is what the
            grid tile uses — but opening one should walk you through the room,
            which is the whole point of a 3D design.

            IN A FRAME. `House3DView` reads its project from the same Redux
            store the editor uses, so mounting it here would load someone
            else's project into the store this page shares with the builder. A
            frame has its own, and cannot reach this one. `?embed=1` drops the
            share page's top bar so only the viewer shows.

            No swipe or zoom handlers on it: the scene has its own orbit and
            pinch gestures, and a page-turn fired from inside a walkthrough
            would be maddening. Paging is the arrows and the keyboard. */}
        {isDesign && item.projectId && item.ownerUid ? (
          <iframe
            className="glb-img glb-frame"
            title={item.description || t('dg_tab_3d')}
            src={`/share/${item.ownerUid}/${item.projectId}?embed=1`}
            allow="fullscreen"
          />
        ) : (
        <img
          ref={imgRef}
          className={`glb-img${loading && !fullUrls[blobId] ? ' glb-img--loading' : ''}${(dx || scale > 1) ? ' glb-img--dragging' : ''}`}
          src={src}
          alt={item.description || ''}
          style={{
            // `pan-y` yields vertical panning to the browser and claims the
            // horizontal axis for the swipe. In FULL VIEW it becomes `none`:
            // pinching needs both axes, and the browser's own page zoom would
            // otherwise take the gesture before this ever sees it.
            touchAction: zoom ? 'none' : 'pan-y',
            // One transform for both jobs — the swipe's offset when at 1x, the
            // zoom and its pan above it. Two transform properties on one
            // element would silently overwrite each other.
            ...(scale > 1
              ? { transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${scale})` }
              : (dx ? { transform: `translateX(${dx}px)` } : null)),
            cursor: zoom ? (scale > 1 ? 'grab' : 'zoom-in') : 'zoom-in',
          }}
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
          onClick={onPhotoClick}
          draggable={false}
        />
        )}

        {/* ── PRODUCT, ON THE PHOTO ────────────────────────────────────────
            A listing's price and description belong ON the picture, in the
            corners, the way every marketplace shows them — below it they read
            as a caption about something rather than a label on it, and the
            price in particular is the first thing looked for.

            Positioned off the measured picture rectangle, not the element box
            — see the effect above. `pointer-events: none` so the photo keeps
            click-to-zoom underneath them.

            Only for a listing. A designer's photo has neither field, keeps its
            caption below, and renders none of this. */}
        {isProduct && (
          <div className="glb-onphoto">
            {item.price != null && (
              <span className="glb-onprice">
                {item.price} {currencySymbolOf(item.currency)}
              </span>
            )}
            {/* THE NAME ALONE, on a scrim across the foot of the picture —
                the same treatment the feed tiles use, so a product looks like
                itself wherever it is seen. The description moved to the top of
                the comments panel: it is a paragraph, and a paragraph printed
                over the picture covered the thing being sold. */}
            {item.title && <span className="glb-onname">{item.title}</span>}

            {/* ADD TO BASKET, on the picture with the price and description.
                All three describe the same object, and the action belongs
                beside the thing rather than in the row of counters below,
                where it read as one more statistic.

                Bottom-RIGHT: the two labels take the left column, and on a
                phone this corner is where the thumb already is. The overlay
                around it is inert (`pointer-events: none`), so this one
                element takes them back — see the CSS. */}
            <BasketButton item={item} className="glb-basket" />
          </div>
        )}

        {/* ── WHERE YOU ARE IN THE POST ─────────────────────────────────────
            Dots, only when there is more than one picture. The arrows and the
            swipe already move through them; this is the part you cannot infer
            from a photograph — that there are four more behind it.

            Clickable, so a specific picture is one press away rather than four.
            Outside .glb-onphoto because that layer is a listing's labels and
            these belong to every post, product or not. */}
        {imgCount > 1 && (
          <div className="glb-dots" role="tablist" aria-label={t('gal_images')}>
            {blobIds.map((bid, i) => (
              <button
                key={bid}
                type="button"
                role="tab"
                aria-selected={i === imgAt}
                aria-label={`${i + 1} / ${imgCount}`}
                className={`glb-dot${i === imgAt ? ' glb-dot--on' : ''}`}
                onClick={(e) => { e.stopPropagation(); setImgAt(i); }}
              />
            ))}
          </div>
        )}

        {/* A designer's caption stays where it was, under the picture. */}
        {item.description && !isProduct && (
          <figcaption className="glb-caption">{item.description}</figcaption>
        )}

        {/* ── WHERE TO BUY ── under the photo, deliberately. The price and the
            description are labels on the thing; the link is an action that
            leaves the site, and an action printed over the picture is one you
            hit while reaching to zoom. Above the byline and the counters
            though: someone who opened a product wants it before the thread. */}
        {item.productUrl && (
          <div className="glb-product">
            <a
              className="glb-buy"
              href={productHref(item.productUrl)}
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              {t('gal_buy')}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M7 17 17 7" /><path d="M9 7h8v8" />
              </svg>
            </a>
          </div>
        )}

        {/* Author and counters on ONE line under the photo. Stacked they took
            two rows of a viewer whose whole job is showing the picture.

            The like bar is photos-only: a published 3D design opens its own
            page, which carries the same bar. The byline is not — every item
            deserves an author, and it is the route from work you like to the
            person who made it. */}
        <div className="glb-meta">
          {item.ownerUid && <OwnerBadge uid={item.ownerUid} />}
          {social && (
            <SocialBar
              kind={socialKind}
              id={socialId}
              ownerUid={item.ownerUid}
              compact
              // The live figure once the panel is listening; until then the
              // one denormalised onto the photo, which arrived with the item.
              // So the count is right before the thread has been opened —
              // which, on a phone, is most of the time.
              commentsCount={commentCount ?? item.commentsCount ?? 0}
              commentsOpen={showComments}
              onComments={() => setShowComments(v => !v)}
              onLikeChange={onLikeChange}
            />
          )}

          {/* SEND THIS TO SOMEONE. LAST IN THE ROW, past the counters: those
              are what the row is for — who made this and how it has been
              received — and an action parked in the middle of them read as one
              more statistic. At the end it is the one thing you can DO here,
              and on a phone (where the row justifies to its two ends) it lands
              under the thumb rather than in the middle of the screen.

              · SIGNED-IN ONLY — the picker chooses from your own conversations,
                and the card is a message, which needs a sender.
              · PUBLISHED ONLY. The card opens the owner's profile at this item
                and that listing is visible-only, so on a hidden item it would
                send someone a link to a page that does not show it. Visitors
                never see a hidden item; the OWNER's own lightbox does. */}
          {user?.uid && item.ownerUid && item.visible !== false && (
            <button
              type="button"
              className="glb-share"
              onClick={(e) => { e.stopPropagation(); setToChat(true); }}
              title={t('share_to_chat')}
              aria-label={t('share_to_chat')}
            >
              <SendIcon size={16} />
            </button>
          )}
        </div>
      </figure>

        {/* UNMOUNTED WHEN CLOSED, not hidden. The panel holds the one live
            listener this feature opens, so a visitor who only wanted to look
            at the picture never subscribes at all. Hiding it with CSS would
            keep that listener running for everyone who opens anything. */}
        {social && showComments && (
          <aside className="glb-side">
            <Comments
              col={commentCol}
              itemId={item.id}
              ownerUid={item.ownerUid}
              onCount={setCommentCount}
              // A LISTING's description reads here, above the thread, rather
              // than over the picture — see the note in Comments. Only a
              // listing: a photo's caption still sits under the image, where a
              // single line belongs.
              caption={isProduct ? item.description || null : null}
            />
          </aside>
        )}
      </div>

      {/* Portalled to the body by ShareToChat itself, so the viewer's own
          close-on-backdrop-click cannot reach it. */}
      {toChat && (
        <ShareToChat
          share={shareFromGalleryItem(item, itemKind)}
          thumbSrc={item.thumbUrl || ''}
          onClose={() => setToChat(false)}
        />
      )}
    </div>
  );
}
